import { decodeLatLngBounds } from './urlHelpers';

/**
 * Single source of truth for the Airport Lounges hub landing page
 * (route: /airport-lounges). Built on the same conventions as
 * `cityLandingPageConfig.js`, but scoped to all of Europe instead of one
 * Swiss city, so it can later be specialised per airport (/airport-lounges/:airport)
 * without touching the duck or the page component.
 *
 * Lounge listings are identified by `publicData.categoryLevel1 = 'lounge'`
 * (and/or a listingType matching /lounge|airport|flughafen/i). They carry an
 * IATA code in the title — e.g. "Zurich Airport (ZRH)" — and, when imported via
 * the LoungePair affiliate pipeline, a `publicData.externalBookingUrl` deeplink.
 */

/**
 * Bounding box covering European airports.
 * Format: ne_lat,ne_lng,sw_lat,sw_lng (see `decodeLatLngBounds`).
 * Reaches from North Cape / Eastern Europe (NE) to the Canary Islands /
 * south-west Atlantic (SW) so every European hub is inside the box.
 */
export const EUROPE_BOUNDS = '71.0,40.0,34.0,-25.0';

/**
 * The `publicData.categoryLevel1` value the SDK query filters on. The hosted
 * category tree uses the id `lounge` (matched case-insensitively by
 * /lounge|airport|flughafen/ in the city taxonomy). If a marketplace stores a
 * different id, change it here only.
 */
export const LOUNGE_CATEGORY = 'lounge';

/** Case-insensitive matcher reused for client-side defensiveness / grouping. */
export const LOUNGE_MATCHER = /lounge|airport|flughafen/i;

/**
 * Curated ordering of major European hubs. Drives the finder datalist, the
 * preferred order of the filter pills and the finder's scoped SearchPage
 * fallback. This is also the seed list for future per-airport landing pages,
 * so keep it sorted by relevance.
 *
 * `bounds` (ne_lat,ne_lng,sw_lat,sw_lng) is the metro box used when the finder
 * or a see-all link scopes a lounge search to this airport. Where the airport
 * belongs to a LOUNGE_CITIES city, the box is that city's box; the remaining
 * hubs got their own boxes (each verified live to return > 0 lounges on prod).
 *
 * `city` is the English name and stays the identifier (finder matching, search scopes);
 * `cityDe` is the German exonym where it differs (Genf, München, Rom, Wien, Kopenhagen), shown on
 * the German page through `airportCityLabel`.
 */
export const POPULAR_AIRPORTS = [
  {
    iata: 'ZRH',
    name: 'Zürich Airport',
    city: 'Zürich',
    country: 'CH',
    bounds: '47.510,8.745,47.260,8.340',
  },
  {
    iata: 'GVA',
    name: 'Geneva Airport',
    city: 'Geneva',
    cityDe: 'Genf',
    country: 'CH',
    bounds: '46.305,6.290,46.130,5.970',
  },
  {
    iata: 'LHR',
    name: 'London Heathrow',
    city: 'London',
    country: 'GB',
    bounds: '51.620,0.100,51.050,-0.620',
  },
  {
    iata: 'CDG',
    name: 'Paris Charles de Gaulle',
    city: 'Paris',
    country: 'FR',
    bounds: '49.030,2.640,48.680,2.250',
  },
  {
    iata: 'FRA',
    name: 'Frankfurt Airport',
    city: 'Frankfurt',
    country: 'DE',
    bounds: '50.180,8.800,49.950,8.380',
  },
  {
    iata: 'AMS',
    name: 'Amsterdam Schiphol',
    city: 'Amsterdam',
    country: 'NL',
    bounds: '52.380,4.880,52.240,4.640',
  },
  {
    iata: 'MUC',
    name: 'Munich Airport',
    city: 'Munich',
    cityDe: 'München',
    country: 'DE',
    bounds: '48.430,11.900,48.280,11.650',
  },
  {
    iata: 'FCO',
    name: 'Rome Fiumicino',
    city: 'Rome',
    cityDe: 'Rom',
    country: 'IT',
    bounds: '41.950,12.700,41.700,12.100',
  },
  {
    iata: 'BCN',
    name: 'Barcelona El Prat',
    city: 'Barcelona',
    country: 'ES',
    bounds: '41.470,2.330,41.180,1.900',
  },
  {
    iata: 'MAD',
    name: 'Madrid Barajas',
    city: 'Madrid',
    country: 'ES',
    bounds: '40.700,-3.300,40.290,-3.820',
  },
  {
    iata: 'VIE',
    name: 'Vienna Airport',
    city: 'Vienna',
    cityDe: 'Wien',
    country: 'AT',
    bounds: '48.320,16.650,48.050,16.200',
  },
  {
    iata: 'IST',
    name: 'Istanbul Airport',
    city: 'Istanbul',
    country: 'TR',
    bounds: '41.450,29.560,40.760,28.520',
  },
  {
    iata: 'CPH',
    name: 'Copenhagen Airport',
    city: 'Copenhagen',
    cityDe: 'Kopenhagen',
    country: 'DK',
    bounds: '55.700,12.750,55.540,12.500',
  },
];

/**
 * European lounge cities for the visual "by city" card grid. The set = the top
 * international hubs by real cowork24.ch lounge inventory plus the two Swiss
 * home-market cities (Geneva, Zürich). Per-city lounge counts are fetched LIVE
 * at load time (see the duck's count queries) — this list only declares each
 * city's identity, airports, metro bounding box (ne_lat,ne_lng,sw_lat,sw_lng
 * covering all its airports), and Unsplash landmark photo. Default order is by
 * known inventory; the component re-sorts by the live count and hides cities
 * with zero lounges. `image` host must stay images.unsplash.com (CSP imgSrc).
 *
 * `name` is the English name and the key everything else uses (the duck's live counts, the pill
 * keys) — do not translate it. `nameDe` is the German exonym where it differs, shown on the German
 * page through `loungeCityLabel`: German searchers type "Lounge Flughafen Genf", not "Geneva".
 */
export const LOUNGE_CITIES = [
  {
    name: 'London',
    country: 'GB',
    airports: ['LHR', 'LGW'],
    bounds: '51.620,0.100,51.050,-0.620',
    image: 'https://images.unsplash.com/photo-1749767138102-f5e0fea51e9f',
  },
  {
    name: 'Istanbul',
    country: 'TR',
    airports: ['IST', 'SAW'],
    bounds: '41.450,29.560,40.760,28.520',
    image: 'https://images.unsplash.com/photo-1560856311-69382e596cd4',
  },
  {
    name: 'Madrid',
    country: 'ES',
    airports: ['MAD'],
    bounds: '40.700,-3.300,40.290,-3.820',
    image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4',
  },
  {
    name: 'Frankfurt',
    country: 'DE',
    airports: ['FRA'],
    bounds: '50.180,8.800,49.950,8.380',
    image: 'https://images.unsplash.com/photo-1754444215850-accb34b9975d',
  },
  {
    name: 'Rome',
    nameDe: 'Rom',
    country: 'IT',
    airports: ['FCO'],
    bounds: '41.950,12.700,41.700,12.100',
    image: 'https://images.unsplash.com/photo-1752886355870-17e8ebe79a05',
  },
  {
    name: 'Barcelona',
    country: 'ES',
    airports: ['BCN'],
    bounds: '41.470,2.330,41.180,1.900',
    image: 'https://images.unsplash.com/photo-1758471206484-0eaa2568320c',
  },
  {
    name: 'Vienna',
    nameDe: 'Wien',
    country: 'AT',
    airports: ['VIE'],
    bounds: '48.320,16.650,48.050,16.200',
    image: 'https://images.unsplash.com/photo-1578400889704-bbd63485d516',
  },
  {
    name: 'Athens',
    nameDe: 'Athen',
    country: 'GR',
    airports: ['ATH'],
    bounds: '38.100,24.150,37.800,23.550',
    image: 'https://images.unsplash.com/photo-1603565816030-6b389eeb23cb',
  },
  {
    name: 'Geneva',
    nameDe: 'Genf',
    country: 'CH',
    airports: ['GVA'],
    bounds: '46.305,6.290,46.130,5.970',
    image: 'https://images.unsplash.com/photo-1752346168893-99056677e672',
  },
  {
    name: 'Zürich',
    country: 'CH',
    airports: ['ZRH'],
    bounds: '47.510,8.745,47.260,8.340',
    image: 'https://images.unsplash.com/photo-1554899199-f6d99e6be6f7',
  },
  {
    name: 'Milan',
    nameDe: 'Mailand',
    country: 'IT',
    airports: ['MXP'],
    bounds: '45.700,9.400,45.400,8.600',
    image: 'https://images.unsplash.com/photo-1681671478309-203dc788c7f4',
  },
  {
    name: 'Manchester',
    country: 'GB',
    airports: ['MAN'],
    bounds: '53.550,-2.050,53.250,-2.450',
    image: 'https://images.unsplash.com/photo-1657700819262-18792be64208',
  },
];

/**
 * Hero / Open Graph / LoungeBreaker image (Unsplash — CSP imgSrc allows
 * images.unsplash.com; plus.unsplash.com premium photos are blocked). A real
 * premium airport-lounge interior — designer armchairs, sculptural chandelier,
 * warm light — i.e. the product being sold, not a terminal corridor. The red
 * seating also harmonises with the cowork24 coral brand. Decorative; safe to
 * swap for a self-hosted asset later.
 */
export const LOUNGE_HERO_URL =
  'https://images.unsplash.com/photo-1692405245790-a9aba01a4fc3?w=1200&h=630&fit=crop&crop=center';

/* Unsplash helpers moved to util/unsplashImages.js — they are generic and are
   shared with the city and space-owner landing pages. Import them from there. */

/**
 * @param {string} boundsStr
 * @returns {object|null} SDK LatLngBounds or null
 */
export const getBoundsForSdk = boundsStr => decodeLatLngBounds(boundsStr);

/**
 * Resolve an IATA code to a search scope. Prefers the LOUNGE_CITIES metro box
 * (covers multi-airport cities like London LHR+LGW), falls back to the
 * POPULAR_AIRPORTS box.
 *
 * @param {string} iata airport code, e.g. 'ZRH'
 * @returns {{bounds: string, name: string}|null} bounds string + display name
 */
export const getBoundsForAirport = iata => {
  const code = (iata || '').toUpperCase();
  if (!code) {
    return null;
  }
  const city = LOUNGE_CITIES.find(c => c.airports.includes(code));
  if (city) {
    return { bounds: city.bounds, name: city.name };
  }
  const airport = POPULAR_AIRPORTS.find(a => a.iata === code);
  return airport ? { bounds: airport.bounds, name: airport.city } : null;
};

const isGerman = locale => `${locale || ''}`.toLowerCase().startsWith('de');

/**
 * Display name of a LOUNGE_CITIES entry in the visitor's language: the German exonym on the
 * German page, the English name elsewhere.
 *
 * @param {{name: string, nameDe?: string}} city
 * @param {string} locale intl.locale
 * @returns {string}
 */
export const loungeCityLabel = (city, locale) =>
  (isGerman(locale) && city?.nameDe) || city?.name || '';

/**
 * Display name of a POPULAR_AIRPORTS entry's city, same rule as loungeCityLabel.
 *
 * @param {{city: string, cityDe?: string}} airport
 * @param {string} locale intl.locale
 * @returns {string}
 */
export const airportCityLabel = (airport, locale) =>
  (isGerman(locale) && airport?.cityDe) || airport?.city || '';
